/**
 * KeyboardShortcutsHelp - Modal showing available keyboard shortcuts
 * 
 * Triggered by pressing '?' anywhere in the app.
 * Designed for power users who want to process leads quickly.
 */

import React from 'react';
import { X, Keyboard, Navigation, Zap, Settings } from 'lucide-react';
import { KEYBOARD_SHORTCUTS, KeyboardShortcut } from '../../hooks/useKeyboardNavigation';

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsHelp({ isOpen, onClose }: KeyboardShortcutsHelpProps) {
  if (!isOpen) return null;

  const groupedShortcuts = KEYBOARD_SHORTCUTS.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, KeyboardShortcut[]>);

  const categoryIcons = {
    navigation: Navigation,
    actions: Zap,
    general: Settings,
  };

  const categoryLabels = {
    navigation: 'Navigation',
    actions: 'Actions',
    general: 'General',
  };

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between bg-gradient-to-r from-[var(--eq-purple)]/5 to-[var(--eq-teal)]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center">
              <Keyboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                Keyboard Shortcuts
              </h2>
              <p className="text-xs text-black/50">Power user navigation</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-black/40" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {Object.entries(groupedShortcuts).map(([category, shortcuts]) => {
            const Icon = categoryIcons[category as keyof typeof categoryIcons];
            const label = categoryLabels[category as keyof typeof categoryLabels];
            
            return (
              <div key={category}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-[var(--eq-purple)]" />
                  <span className="text-xs font-semibold text-black/40 uppercase tracking-wide">
                    {label}
                  </span>
                </div>
                <div className="space-y-2">
                  {shortcuts.map((shortcut) => (
                    <div 
                      key={shortcut.key}
                      className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-black/[0.02] transition-colors"
                    >
                      <span className="text-sm text-black/70">{shortcut.description}</span>
                      <div className="flex items-center gap-1">
                        {shortcut.key.split(' / ').map((key, idx) => (
                          <React.Fragment key={key}>
                            {idx > 0 && <span className="text-black/20 text-xs mx-1">or</span>}
                            <kbd className="px-2 py-1 text-xs font-mono bg-black/5 text-black/60 rounded border border-black/10">
                              {key}
                            </kbd>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-black/5 bg-[#f8f8f6]">
          <p className="text-xs text-black/40 text-center">
            Press <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-black/5 rounded border border-black/10">?</kbd> anytime to toggle this help
          </p>
        </div>
      </div>
    </div>
  );
}

export default KeyboardShortcutsHelp;

